import React, { useState, useEffect } from 'react';
import {
  Modal, Form, Button, Row, Col, Alert, Spinner, InputGroup, Badge
} from 'react-bootstrap';
import { Save, X, DollarSign, CreditCard } from 'lucide-react';
import customOrderService from '../../../services/customOrderService';

/**
 * ✅ ATELIER: Modal d'ajout d'un paiement sur une commande.
 */
const AddPaymentModal = ({ show, onHide, order, onSuccess }) => {

  const [formData, setFormData] = useState({
    amount: '',
    paymentMethod: 'CASH',
    referenceNumber: '',
    notes: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (show && order) {
      // Prérempli avec le solde restant pour faciliter le "tout payer"
      setFormData({
        amount: order.remainingAmount || '',
        paymentMethod: 'CASH',
        referenceNumber: '',
        notes: ''
      });
      setError(null);
    }
  }, [show, order]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAmountChange = (value) => {
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      handleChange('amount', value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const amount = parseFloat(formData.amount);
    if (!amount || amount <= 0) {
      setError('Le montant est invalide');
      return;
    }

    if (amount > order.remainingAmount) {
      setError(
        `Le montant dépasse le solde restant (${customOrderService.formatPrice(order.remainingAmount)})`
      );
      return;
    }

    setLoading(true);

    try {
      const response = await customOrderService.addPayment(order.id, formData);

      if (response.success) {
        console.log('✅ Paiement ajouté');
        onSuccess?.(response.data);
        onHide();
      } else {
        setError(response.message || 'Erreur lors de l\'ajout');
      }
    } catch (err) {
      setError(err.message || 'Erreur inattendue');
    } finally {
      setLoading(false);
    }
  };

  // Déterminer si ce paiement solderait la commande
  const amountNum = parseFloat(formData.amount) || 0;
  const willSettle = order && amountNum > 0 && amountNum >= order.remainingAmount;

  if (!order) return null;

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="d-flex align-items-center gap-2">
          <DollarSign size={20} className="text-success" />
          Ajouter un paiement
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Résumé commande */}
          <div className="bg-light rounded p-3 mb-3">
            <div className="small text-muted mb-1">Commande</div>
            <div className="fw-bold mb-2">{order.orderNumber} — {order.customerName}</div>

            <Row className="g-2 small">
              <Col xs={6}>
                <div className="text-muted">Total :</div>
                <strong>{customOrderService.formatPrice(order.totalPrice)}</strong>
              </Col>
              <Col xs={6}>
                <div className="text-muted">Déjà payé :</div>
                <strong className="text-success">
                  {customOrderService.formatPrice(order.advanceAmount)}
                </strong>
              </Col>
              <Col xs={12} className="border-top pt-2 mt-1">
                <div className="text-muted">Solde restant :</div>
                <strong className="text-warning" style={{ fontSize: '1.1rem' }}>
                  {customOrderService.formatPrice(order.remainingAmount)}
                </strong>
              </Col>
            </Row>
          </div>

          {/* Montant */}
          <Form.Group className="mb-3">
            <Form.Label>
              Montant <span className="text-danger">*</span>
            </Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                inputMode="decimal"
                placeholder="0"
                value={formData.amount}
                onChange={(e) => handleAmountChange(e.target.value)}
                autoFocus
              />
              <InputGroup.Text>FCFA</InputGroup.Text>
            </InputGroup>
            {willSettle && (
              <div className="mt-2">
                <Badge bg="success" className="d-inline-flex align-items-center gap-1">
                  ✓ Ce paiement soldera entièrement la commande
                </Badge>
              </div>
            )}
          </Form.Group>

          {/* Méthode de paiement */}
          <Form.Group className="mb-3">
            <Form.Label>
              <CreditCard size={14} className="me-1" />
              Méthode de paiement <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={formData.paymentMethod}
              onChange={(e) => handleChange('paymentMethod', e.target.value)}
            >
              <option value="CASH">💵 Espèces</option>
              <option value="WAVE">📱 Wave</option>
              <option value="ORANGE_MONEY">🟠 Orange Money</option>
              <option value="FREE_MONEY">🟡 Free Money</option>
              <option value="BANK_TRANSFER">🏦 Virement bancaire</option>
              <option value="CHECK">📄 Chèque</option>
              <option value="CARD">💳 Carte</option>
            </Form.Select>
          </Form.Group>

          {/* Référence */}
          <Form.Group className="mb-3">
            <Form.Label>Référence (optionnel)</Form.Label>
            <Form.Control
              type="text"
              placeholder="Ex: WV123456, N° chèque..."
              value={formData.referenceNumber}
              onChange={(e) => handleChange('referenceNumber', e.target.value)}
            />
          </Form.Group>

          {/* Notes */}
          <Form.Group className="mb-3">
            <Form.Label>Notes (optionnel)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Observations..."
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={loading}>
            <X size={16} className="me-1" />
            Annuler
          </Button>
          <Button type="submit" variant="success" disabled={loading}>
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save size={16} className="me-1" />
                Enregistrer le paiement
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AddPaymentModal;